import AlertPopup from "components/alert-popup/AlertPopup";
import ClientsBillingClient from "components/sections-billing-client/ClientsBillingClient";
import InfoContext from "pages/InfoContext";
import InfoHeaderAction from "pages/info-header/InfoHeaderAction";
import PersonalBillingClient from "components/sections-billing-client/PersonalBillingClient";
import { ENTITY } from "constants/info-header";
import {
  FILTERS_FOR_NAMES,
  POPUP_STATUS,
  QUERY_STATUS,
  SECTIONS,
} from "constants/constants";
import { useBillingClientContext } from "api/directory/billing-client/BillingClientProvider";
import { useBillingClientDatatrackContext } from "api/datatrack/client/BillingClientDatatrackProvider";
import { useClientContext } from "api/directory/client/ClientProvider";
import { useCountryContext } from "api/settings/country/CountryProvider";
import { useContext, useEffect, useMemo, useState } from "react";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useNavigate, useParams } from "react-router-dom";
import { useSettingsContext } from "api/settings/SettingsProvider";
import { useUserContext } from "api/user/UserProvider";
import PageContext from "pages/PageContext";
import ValidationModal from "components/modal/ValidationModal";
import {
  commonOnCloseHandler,
  validationModalTitleHelper,
} from "utils/modalsHelper";
import { statusHandler } from "utils/helper";
import LoaderV2 from "core/loader/LoaderV2";

const DisplayBillingClient = () => {
  const {
    dataCountry,
    getDataCountry,
    dataTvaRegime,
    getDataTvaRegime,
    dataPaymentCondition,
    getDataPaymentCondition,
  } = useSettingsContext();
  const { countryList, countryListGetter } = useCountryContext();
  const { lang, userScope } = useUserContext();

  const navigate = useNavigate();
  const {
    searchDavidsonBySiren,
    sirenData,
    setSirenData,
    searchUsers,
    usersByIds,
    sirenErrorMessage,
    setSirenErrorMessage,
  } = useGeneralContext();
  const {
    getBillingClientById,
    updateBillingClient,
    editBillingClientStatus,
    displayBillingClientStatus,
    deleteBillingClient,
    getBillingClientBySlug,
    billingClientBySlug,
    refresh,
    setRefresh,
    setEditBillingClientStatus,
    deleteBillingClientStatus,
    setDeleteBillingClientStatus,
  } = useBillingClientContext();
  const [section, setSection] = useState(SECTIONS.GENERAL_INFO);
  const [readOnly, setReadOnly] = useState(true);
  const [code, setCode] = useState();
  const [status, setStatus] = useState();
  const { slug } = useParams();
  const { userRoleList } = useUserContext();
  const { getClientsListByBillingClientId, clientsListByBillingClientId } =
    useBillingClientDatatrackContext();
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const { getClientsByIds, clientsByIds } = useClientContext();
  const { slugSearched } = useContext(PageContext);
  const billingSlug = slugSearched || slug;
  const isCurrentEntity = billingClientBySlug?.slug === billingSlug;
  const [isOpen, setIsOpen] = useState(false);

  const currentQueryStatus = statusHandler({
    statusList: [editBillingClientStatus, deleteBillingClientStatus],
  });

  const validationModalTitle = validationModalTitleHelper({
    isEditAction: editBillingClientStatus !== QUERY_STATUS.INITIAL,
    isDeleteAction: deleteBillingClientStatus !== QUERY_STATUS.INITIAL,
  });

  useEffect(() => {
    setReadOnly(true);
    getBillingClientBySlug(slugSearched || slug);
  }, [slug, slugSearched, refresh]);

  useEffect(() => {
    if (displayBillingClientStatus === QUERY_STATUS.NOT_FOUND) {
      navigate(`/${userScope}/not-found`, { replace: true });
    }
  }, [displayBillingClientStatus, userScope, navigate]);

  useEffect(() => {
    const ids = clientsListByBillingClientId
      ?.filter(Boolean)
      .map((item) => item.client_id);
    if (billingClientBySlug)
      switch (section) {
        case SECTIONS.CLIENTS:
          if (ids?.length > 0) {
            getNamesByFilters({
              types: [FILTERS_FOR_NAMES.CLIENT],
              id: ids,
            });
          }
          break;
        case SECTIONS.GENERAL_INFO:
          billingClientBySlug?.id &&
            getBillingClientById(billingClientBySlug?.id);
          break;
        default:
          break;
      }
    setRefresh(false);
  }, [billingClientBySlug, section, refresh]);

  const updateData = async (info) => {
    window.scrollTo(0, 0);
    // Awaited so the caller's submit lock covers the whole request
    try {
      await updateBillingClient(billingClientBySlug?.id, {
        ...info,
        nationality_id: info?.nationality_id[0]?.value,
        billing_country:
          info?.billing_country[0]?.value || info?.billing_country,
      });
    } catch {
      // Provider already set the entity status to ERROR and apiErrorResponse
    }
  };

  const onSubmit = async (info) => {
    await updateData(info);
  };

  useEffect(() => {
    if (billingClientBySlug?.id) {
      getClientsListByBillingClientId({
        id: billingClientBySlug?.id,
        scope: userScope,
      });
    }
  }, [getClientsByIds, billingClientBySlug]);

  useEffect(() => {
    if (clientsListByBillingClientId?.length > 0) {
      getClientsByIds({
        ids: clientsListByBillingClientId
          .map((client) => client.client_id)
          .toString(),
      });
    }
  }, [clientsListByBillingClientId, getClientsByIds]);

  useEffect(() => {
    countryListGetter();
    getDataCountry();
    getDataTvaRegime();
    getDataPaymentCondition();
    setSirenData({});
  }, [
    getDataCountry,
    getDataTvaRegime,
    getDataPaymentCondition,
    countryListGetter,
    setSirenData,
  ]);

  useEffect(() => {
    currentQueryStatus !== QUERY_STATUS.INITIAL && setIsOpen(true);
    if (editBillingClientStatus === QUERY_STATUS.SUCCESS) {
      getBillingClientBySlug(billingClientBySlug?.slug);
    }
  }, [currentQueryStatus]);

  useEffect(() => {
    if (sirenErrorMessage) {
      setStatus(POPUP_STATUS.ERROR);
      setCode(sirenErrorMessage);
    }
  }, [sirenErrorMessage]);

  const value = useMemo(
    () => ({
      billingClientBySlug,
      countryList,
      dataCountry,
      dataPaymentCondition,
      dataTvaRegime,
      deleted: billingClientBySlug?.deleted,
      entity: ENTITY.BILLING_CLIENT,
      id: billingClientBySlug?.id,
      lang,
      navigate,
      onDelete: deleteBillingClient,
      readOnly,
      searchDavidsonBySiren,
      searchUsers,
      section,
      userRoleList,
      setReadOnly,
      setSection,
      setSirenData,
      sirenData,
      usersByIds,
      sirenErrorMessage,
      setSirenErrorMessage,
      setStatus,
      setCode,
      code,
      slug,
    }),
    [
      billingClientBySlug,
      countryList,
      dataCountry,
      dataPaymentCondition,
      dataTvaRegime,
      lang,
      readOnly,
      section,
      sirenData,
      usersByIds,
      sirenErrorMessage,
      setSirenErrorMessage,
      deleteBillingClient,
      navigate,
      searchDavidsonBySiren,
      searchUsers,
      setSirenData,
      userRoleList,
      setStatus,
      setCode,
      code,
      slug,
    ]
  );

  return (
    <InfoContext.Provider value={value}>
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <ValidationModal
        open={isOpen}
        status={currentQueryStatus}
        messages={{
          pendingTitleId: validationModalTitle,
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: validationModalTitle,
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: validationModalTitle,
          errorMessageId: "modal_validation_file_error_msg",
        }}
        onClose={() =>
          commonOnCloseHandler({
            editEntityStatus: editBillingClientStatus,
            deleteEntityStatus: deleteBillingClientStatus,
            setDeleteEntityStatus: setDeleteBillingClientStatus,
            setEditEntityStatus: setEditBillingClientStatus,
            setIsOpen,
            setReadOnly,
            userScope,
            navigate,
          })
        }
      />
      {(!isCurrentEntity ||
        ![
          QUERY_STATUS.SUCCESS,
          QUERY_STATUS.ERROR,
          QUERY_STATUS.NOT_FOUND,
        ].includes(displayBillingClientStatus)) && (
        <LoaderV2 loaderSize="fullpage" />
      )}
      {isCurrentEntity &&
        displayBillingClientStatus === QUERY_STATUS.SUCCESS && (
          <>
            <InfoHeaderAction />
            {section === SECTIONS.GENERAL_INFO && (
              <PersonalBillingClient
                id={billingClientBySlug?.id}
                onSubmit={onSubmit}
                readOnly={readOnly}
                setReadOnly={setReadOnly}
                getStatus={setStatus}
                getCode={setCode}
                status={status}
                code={code}
              />
            )}
            {section === SECTIONS.CLIENTS && (
              <ClientsBillingClient
                clients={clientsByIds || []}
                list={clientsListByBillingClientId || []}
                lang={lang}
                userScope={userScope}
                namesByFilters={namesByFilters}
              />
            )}
          </>
        )}
    </InfoContext.Provider>
  );
};
export default DisplayBillingClient;
